import type { CSSProperties, ReactElement, ReactNode } from "react";
import { EmptyState } from "./EmptyState.js";
import { ErrorState } from "./ErrorState.js";
import { LoadingState } from "./LoadingState.js";

/**
 * Sprint 31 (GT-TENANT-ADMIN-01) — shared `<DataTable>` primitive.
 *
 * Promotes the documented table contract from `docs/design/TABLES.md`
 * (Sprint 29.5B) into a single component. Same visual rules — sticky
 * header, distinct header band, hover, padding, sticky semantics —
 * but in one place so future tables don't have to re-derive them.
 *
 * Loading / empty / error states delegate to the existing shared
 * primitives (`<LoadingState>`, `<EmptyState>`, `<ErrorState>`) so
 * the table is never the place where a one-off state card creeps in.
 *
 * Scope (Sprint 31):
 *   - Platform Admin Tenants (Sprint 31 P3) — first canonical caller.
 *
 * Out of scope for Sprint 31: rolling out across every existing list.
 * The primitive ships, the migration of older tables (Devices /
 * Drivers / Imports / Reports / Alerts) lands opportunistically as
 * those pages get touched. See SHARED_COMPONENT_INVENTORY entry #2.
 */

export type ColumnAlign = "start" | "end" | "center";

export interface DataTableColumn<Row> {
  /** Stable id used as React key + `data-column` on cells. */
  readonly id: string;
  /** Visible header label. */
  readonly header: ReactNode;
  /** Cell renderer. Receives the row + its index. */
  readonly cell: (row: Row, index: number) => ReactNode;
  /** Inline alignment (default: `start`). */
  readonly align?: ColumnAlign;
  /** Optional fixed width (e.g. `"120px"`, `"15ch"`, `"30%"`). */
  readonly width?: string;
  /** Optional accessible label for sort affordances etc. */
  readonly ariaLabel?: string;
}

export interface DataTableProps<Row> {
  /** Visible caption (rendered as a screen-reader-only `<caption>`). */
  readonly caption: string;
  /** Column descriptors. */
  readonly columns: ReadonlyArray<DataTableColumn<Row>>;
  /** Row data. Each row must produce a stable id via `getRowId`. */
  readonly rows: ReadonlyArray<Row>;
  /** Stable id producer — used as React key. */
  readonly getRowId: (row: Row, index: number) => string;

  /** Loading? When true, shows the loading state inside the table area. */
  readonly loading?: boolean;
  /**
   * Error? When set, the error state replaces the table body.
   * String form is treated as the error title; pass an object for
   * full control.
   */
  readonly error?:
    | string
    | {
        readonly title: string;
        readonly body?: ReactNode;
        readonly action?: ReactNode;
      }
    | null;
  /**
   * Empty state. Rendered when `loading` is false, no error, and
   * `rows.length === 0`. Pass `false` to opt out and render an
   * empty `<tbody>` instead.
   */
  readonly empty?:
    | { readonly title: string; readonly body?: ReactNode; readonly action?: ReactNode }
    | false;
  /** Localised label shown by the loading state (default: `"Loading…"`). */
  readonly loadingLabel?: string;
  /** Optional `data-testid` applied to the outer table element. */
  readonly testId?: string;
  /** Optional inline style passed to the outer scroll container. */
  readonly style?: CSSProperties;
  /** Optional row-level `data-testid` producer. */
  readonly rowTestId?: (row: Row, index: number) => string;
  /**
   * Optional override merged onto every `<th>`'s base style, for callers
   * whose reference design uses a denser row raster than the default
   * (e.g. GTCC's `--gt-cmp-gtcc-table-head-size`/`-cell-pad`). Column-level
   * `align`/`width` still win over anything set here.
   */
  readonly headerCellStyle?: CSSProperties;
  /** Same as `headerCellStyle`, but for body `<td>` cells. */
  readonly cellStyle?: CSSProperties;
}

const HEADER_STYLE: CSSProperties = {
  background: "var(--gt-sys-bg-subtle)",
  fontSize: "var(--gt-sys-font-size-xs)",
  fontWeight: "var(--gt-sys-font-weight-semibold)",
  color: "var(--gt-sys-text-muted)",
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  borderBlockEnd: "2px solid var(--gt-sys-border)",
  position: "sticky",
  insetBlockStart: 0,
  zIndex: 1,
  paddingBlock: "var(--gt-sys-space-3)",
  paddingInline: "var(--gt-sys-space-3)",
};

const CELL_STYLE: CSSProperties = {
  fontSize: "var(--gt-sys-font-size-sm)",
  color: "var(--gt-sys-text)",
  borderBlockEnd: "1px solid var(--gt-sys-border)",
  paddingBlock: "var(--gt-sys-space-4)",
  paddingInline: "var(--gt-sys-space-3)",
};

export function DataTable<Row>(props: DataTableProps<Row>): ReactElement {
  const {
    caption,
    columns,
    rows,
    getRowId,
    loading = false,
    error = null,
    empty = { title: "No items", body: undefined },
    loadingLabel = "Loading…",
    testId,
    style,
    rowTestId,
    headerCellStyle,
    cellStyle,
  } = props;

  const showLoading = loading;
  const showError = !showLoading && error !== null && error !== undefined;
  const showEmpty = !showLoading && !showError && rows.length === 0 && empty !== false;

  return (
    <div
      data-testid={testId ? `${testId}-wrap` : undefined}
      style={{
        inlineSize: "100%",
        overflowX: "auto",
        ...style,
      }}
    >
      <table
        data-testid={testId}
        style={{
          inlineSize: "100%",
          borderCollapse: "collapse",
          tableLayout: "auto",
        }}
      >
        <caption
          style={{
            position: "absolute",
            inlineSize: 1,
            blockSize: 1,
            padding: 0,
            margin: -1,
            overflow: "hidden",
            clip: "rect(0,0,0,0)",
            whiteSpace: "nowrap",
            border: 0,
          }}
        >
          {caption}
        </caption>
        <thead>
          <tr>
            {columns.map((col) => (
              <th
                key={col.id}
                scope="col"
                aria-label={col.ariaLabel}
                data-column={col.id}
                style={{
                  ...HEADER_STYLE,
                  ...headerCellStyle,
                  textAlign: col.align ?? "start",
                  inlineSize: col.width,
                }}
              >
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {showLoading ? (
            <tr data-testid={testId ? `${testId}-loading-row` : undefined}>
              <td colSpan={columns.length} style={{ ...CELL_STYLE, padding: 0 }}>
                <LoadingState label={loadingLabel} />
              </td>
            </tr>
          ) : showError ? (
            <tr data-testid={testId ? `${testId}-error-row` : undefined}>
              <td colSpan={columns.length} style={{ ...CELL_STYLE, padding: 0 }}>
                {typeof error === "string" ? (
                  <ErrorState title={error} />
                ) : (
                  <ErrorState
                    title={error.title}
                    {...(error.body !== undefined ? { body: error.body } : {})}
                    {...(error.action !== undefined ? { action: error.action } : {})}
                  />
                )}
              </td>
            </tr>
          ) : showEmpty ? (
            <tr data-testid={testId ? `${testId}-empty-row` : undefined}>
              <td colSpan={columns.length} style={{ ...CELL_STYLE, padding: 0 }}>
                <EmptyState
                  title={empty.title}
                  {...(empty.body !== undefined ? { body: empty.body } : {})}
                  {...(empty.action !== undefined ? { action: empty.action } : {})}
                />
              </td>
            </tr>
          ) : (
            rows.map((row, index) => (
              <tr
                key={getRowId(row, index)}
                data-testid={rowTestId ? rowTestId(row, index) : undefined}
                style={{ transition: "background 75ms ease-out" }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "var(--gt-sys-bg-subtle)")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "")}
              >
                {columns.map((col) => (
                  <td
                    key={col.id}
                    data-column={col.id}
                    style={{
                      ...CELL_STYLE,
                      ...cellStyle,
                      textAlign: col.align ?? "start",
                      inlineSize: col.width,
                    }}
                  >
                    {col.cell(row, index)}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
